import { useCallback, useEffect } from "react";
import { Price, CreatePrice, UpdatePrice } from "../types/models/Price.d";
import { UpdateEntityPrice } from "../types/models/Price";
import { useGetPriceListQuery, useInsertPriceMutation, useUpdatePriceMutation, useDeletePriceMutation, } from "../apis/Price.api";
import { useDeleteDialog, useDescriptionAlert } from '../../share';
import { useNavigate } from "react-router";
import { t } from "@lingui/core/macro";

export default function usePrice(page: number, pageSize: number) {

    const navigate = useNavigate()
    const { openDialog, closeDialog } = useDeleteDialog();

    const { openSuccessAlert, openFailAlert } = useDescriptionAlert()

    const [insertPriceMutation, { 
        isLoading: insertPriceLoading, 
        isSuccess: insertPriceIsSuccess,
        isError: insertPriceIsError,
        error: insertPriceError,
    }] = useInsertPriceMutation()
    const [updatePriceMutation, { 
        isLoading: updatePriceLoading,
        isSuccess: updatePriceIsSuccess,
        isError: updatePriceIsError,
        error: updatePriceError,
    }] = useUpdatePriceMutation()
    const [deletePriceMutation, { 
        isLoading: deletePriceLoading,
        isSuccess: deletePriceIsSuccess,
        isError: deletePriceIsError,
        error: deletePriceError,
    }] = useDeletePriceMutation()

    const loading =
        insertPriceLoading ||
        updatePriceLoading ||
        deletePriceLoading ||
        false

    const insertPrice = useCallback(async (price: CreatePrice, redirectPath: string|undefined) => {
        try {
            const data = await insertPriceMutation(price).unwrap()
            if (data) {
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate(`../${data.id}`, {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("add error", e)
            openFailAlert(t`Add error: ` + e.toString())
        }
    }, [insertPriceMutation, navigate])
    
    useEffect(() => {
        if (insertPriceIsSuccess) {
            openSuccessAlert('Price created')
        } else if (insertPriceIsError) {
            console.log("add error", insertPriceError)
            openFailAlert(t`Price add error: ` + insertPriceError?.data?.message)
        }
    }, [
        insertPriceIsSuccess,
        insertPriceIsError,
        insertPriceError
    ]);

    const updatePrice = useCallback(async (price: UpdateEntityPrice, redirectPath: string|undefined) => {
        try {
            const data = await updatePriceMutation(price).unwrap();
            if (data != null) {
                //if update success
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate('..', {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("update error", e)
            openFailAlert(t`Update error: ` + e.toString())
        }
    }, [updatePriceMutation, navigate])
    
    useEffect(() => {
        if (updatePriceIsSuccess) {
            openSuccessAlert(t`Price updated`)
        } else if (updatePriceIsError) {
            console.log("update error", updatePriceError)
            openFailAlert(t`Price update error: ` + updatePriceError?.data?.message)
        }
    }, [
        updatePriceIsSuccess,
        updatePriceIsError,
        updatePriceError
    ]);

    const deletePrice = useCallback((price: Price, redirectPath: string|undefined) => {
        openDialog(async () => {
            price = {
                ...price,
            }
            try {
                const { data } = await deletePriceMutation(price);
                if (data != null) {
                    if (redirectPath) {
                        navigate(redirectPath, {relative: 'path'})
                    }
                }
            } catch (e) {
                console.log("delete error", e)
                openFailAlert(t`Delete error: ` + e.toString())
            }
            closeDialog()
        });
    }, [deletePriceMutation, closeDialog, openDialog])
    
    useEffect(() => {
        if (deletePriceIsSuccess) {
            openSuccessAlert(t`Price deleted`)
        } else if (deletePriceIsError) {
            console.log("delete error", deletePriceError)
            openFailAlert(t`Price delete error: ` + deletePriceError?.data?.message)
        }
    }, [
        deletePriceIsSuccess,
        deletePriceIsError,
        deletePriceError
    ]);

    return {
        loading,
        insertPrice,
        updatePrice,
        deletePrice
    }
}
